import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DomainOperation, DomainTld } from '../types/models/DomainPricing';
import type {
  ApiResponseDomainOperationPrices,
  ApiResponseDomainOperationsList,
} from '../types/api/ApiResponseDomainPricing';
import type { ClientDomainCell } from './useClientDomainPricing';

interface UseTenantDomainPricesReturn {
  operations: DomainOperation[];
  /** The priced TLDs (union across operations), sorted by label — the matrix rows. */
  tlds: DomainTld[];
  /** Effective cells keyed by `${slug}:${tld}` → { unit_price, is_custom }. */
  priceMap: Map<string, ClientDomainCell>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const cellKey = (slug: string, tld: string) => `${slug}:${tld}`;

// Read-only domain pricing for the in-scope client (their own, or the impersonated one — the
// /prices/domains routes resolve via the impersonation header). Fetches the operation catalog
// (GET /prices/domains) then each operation's effective per-TLD prices (GET /prices/domains/{slug})
// in parallel, and derives the row axis (TLDs) from the union of priced cells — the tenant has no
// access to the staff TLD registry. Re-runs when the in-scope client changes (apiFetch identity).
export const useTenantDomainPrices = (enabled = true): UseTenantDomainPricesReturn => {
  const [operations, setOperations] = useState<DomainOperation[]>([]);
  const [tlds, setTlds] = useState<DomainTld[]>([]);
  const [priceMap, setPriceMap] = useState<Map<string, ClientDomainCell>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opsResponse = await apiFetch('/prices/domains');
      // Branch on the JSend body.status, NEVER response.ok.
      const opsBody = (await opsResponse.json().catch(() => null)) as ApiResponseDomainOperationsList | null;
      if (!(opsBody?.status === 'success' && opsBody.data?.domain_operations)) {
        setError(opsBody?.message ?? 'Failed to load domain operations');
        return;
      }
      const ops = opsBody.data.domain_operations;

      const perOperation = await Promise.all(
        ops.map(async (op) => {
          const response = await apiFetch(`/prices/domains/${op.slug}`);
          const body = (await response.json().catch(() => null)) as ApiResponseDomainOperationPrices | null;
          if (body?.status === 'success' && body.data?.prices) {
            return { slug: op.slug, prices: body.data.prices };
          }
          throw new Error(body?.message ?? `Failed to load prices for "${op.slug}"`);
        }),
      );

      const map = new Map<string, ClientDomainCell>();
      const tldLabels = new Map<string, string>();
      perOperation.forEach(({ slug, prices }) => {
        prices.forEach((p) => {
          map.set(cellKey(slug, p.tld), { unit_price: p.unit_price, is_custom: p.is_custom });
          if (!tldLabels.has(p.tld)) {
            tldLabels.set(p.tld, p.label);
          }
        });
      });
      // Only priced TLDs surface in the responses, so they are all active.
      const rows: DomainTld[] = Array.from(tldLabels.entries())
        .map(([tld, label]) => ({ tld, label, active: true }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setOperations(ops);
      setTlds(rows);
      setPriceMap(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching domain prices');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchAll();
    }
  }, [enabled, auth.isAuthenticated, fetchAll]);

  return { operations, tlds, priceMap, loading, error, refetch: fetchAll };
};
